import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, WifiOff } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface WifiInfoSheetProps {
  open: boolean;
  onClose: () => void;
}
const SECTIONS = [{
  heading: 'Why Wi-Fi needs to be off',
  body: 'Call forwarding is activated using carrier codes that travel over your mobile network. When Wi-Fi calling is on, some carriers route these codes over Wi-Fi instead of the cellular network, where they can be ignored or fail silently. Turning Wi-Fi off makes sure the code reaches your carrier and the forwarding rule is set correctly.'
}, {
  heading: 'How to turn it off',
  body: 'Open Control Center and tap the Wi-Fi icon to disconnect, or go to Settings → Wi-Fi and toggle it off. You only need to do this for the few seconds it takes to activate each rule.'
}, {
  heading: 'Turn it back on right after',
  body: 'Once your AI answering rule is confirmed, you can switch Wi-Fi back on immediately. Forwarding stays active over the cellular network and is unaffected by your Wi-Fi connection from then on.'
}];
export function WifiInfoSheet({
  open,
  onClose
}: WifiInfoSheetProps) {
  const {
    isLight
  } = useTheme();
  return <AnimatePresence data-id="element-659" data-mp="1nmln0jcfex15">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 z-40 bg-black/50 backdrop-blur-sm" data-id="element-660" data-mp="1imzayd8c3mtx" />

          {/* Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 36
      }} drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.4
      }} onDragEnd={(_, info) => {
        if (info.offset.y > 120) onClose();
      }} role="dialog" aria-modal="true" aria-label="About turning off Wi-Fi" className={`absolute inset-x-0 bottom-0 z-50 rounded-t-3xl max-h-[88%] flex flex-col ${isLight ? 'bg-white' : 'bg-[#1c1c1e]'}`} data-id="element-661" data-mp="21kk1rpunsvh6">
            {/* Drag handle */}
            <div className="pt-2.5 pb-1 flex justify-center flex-shrink-0" data-id="element-662" data-mp="3vt5uplmiroxo">
              <div className={`w-9 h-1 rounded-full ${isLight ? 'bg-slate-300' : 'bg-white/20'}`} data-id="element-663" data-mp="4lbanr4gxz0v" />
            </div>

            {/* Header */}
            <div className="px-5 pt-3 flex items-start justify-between gap-3 flex-shrink-0" data-id="element-664" data-mp="1yzubzqd9xwhb">
              <div className="flex items-center gap-2.5" data-id="element-665" data-mp="1yxhx5m2tc28h">
                <div className="p-2 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400" data-id="element-666" data-mp="294kk6dz9twtz">
                  <WifiOff className="w-5 h-5" data-id="element-667" data-mp="24699k115jiap" />
                </div>
                <h2 className={`text-[18px] font-bold leading-snug ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-668" data-mp="2hwcoe6xpc9q4">
                  Why turn off Wi-Fi?
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className={`p-1.5 -mr-1 rounded-full transition-colors flex-shrink-0 ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-669" data-mp="2rspgx1highja">
                <X className="w-5 h-5" data-id="element-670" data-mp="2wuetwmoan5a0" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-5 pt-5" style={{
          paddingBottom: 32
        }} data-id="element-671" data-mp="16hfxerhyjag4">
              {SECTIONS.map((section, i) => <div key={section.heading} data-id="element-672" data-mp="13w86pi2yt920" data-mpi={i}>
                  {i > 0 && <div className={`my-4 border-t ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-673" data-mp="18ujdfjxyr3g0" />}
                  <h3 className={`text-[15px] font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-674" data-mp="1hw0x1cakxn9g">
                    {section.heading}
                  </h3>
                  <p className="text-[13px] leading-[1.5]" style={{
              color: isLight ? '#6B7280' : '#a1a1aa'
            }} data-id="element-675" data-mp="p6twk7rkenlk">
                    {section.body}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}